import { type MouseEvent, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Page from 'components/layouts/page/Page.tsx';
import ButtonGroup from 'components/shared/ButtonGroup.tsx';
import MobilePageHeader from 'components/shared/MobilePageHeader.tsx';
import DesktopOnly from 'components/shared/responsiveness/DesktopOnly.tsx';
import MobileOnly from 'components/shared/responsiveness/MobileOnly.tsx';
import { Flags } from 'constants/flags';
import { useDiscardChanges } from 'context/discardChanges.tsx';
import { Form, FormProvider } from 'context/form/index.tsx';
import { FormHelpers, FormMeta, FormValues } from 'context/form/types.ts';
import { useProfile } from 'context/profile';
import { TranslationProvider } from 'context/translations.tsx';
import { usePushSubscription } from 'hooks/usePushSubscription';
import isDeepEqual from 'lib/isDeepEqual.ts';
import { isReceiving } from 'lib/webPush';
import Client from 'models/Client.ts';
import {
  GeneralPreference,
  InboundReferralPreference,
  Notification,
  NotificationOptions,
} from 'models/Notifications.ts';
import { PERMISSIONS } from 'models/Profile';
import { UserPreferenceType } from 'models/UserPreference';
import { adminFetchUserNotificationPreferences, adminUsersQueryKeys } from 'services/api/admin/users';
import { clientsQueryKeys, indexClients } from 'services/api/client.ts';
import {
  AnyNotificationPreference,
  createGeneralPreference,
  createInboundReferralPreference,
  createNotificationPreferences,
  fetchNotificationPreferences,
  isCareCoordinationPreference,
  isGeneralPreference,
  isInboundReferralPreference,
  notificationPreferencesQueryKeys,
  updateGeneralPreference,
  updateInboundReferralPreference,
  updateNotificationPreferences,
} from 'services/api/notificationPreferences.ts';
import { useToastActions } from 'stores/toastStore.ts';
import { H2 } from 'styles/typography.tsx';
import ChevronDownBold from 'svg/ChevronDownBold';
import ChevronRightBold from 'svg/ChevronRightBold';

import Button from '../shared/Button.tsx';
import Tabs, { TabType } from '../Tabs.tsx';

import ClientSelector from './ClientSelector.tsx';
import GeneralPreferencesContainer, { GENERAL_FORM_KEY } from './GeneralPreferencesContainer.tsx';
import InboundReferralPreferencesContainer, {
  INBOUND_REFERRALS_FORM_KEY,
} from './InboundReferralPreferencesContainer.tsx';
import NoPushDevicesModal from './NoPushDevicesModal.tsx';
import NotificationPreferencesContainer from './NotificationPreferencesContainer.tsx';

// Care-coordination rows are keyed by group-type id (uuids); the single inbound-referrals row
// lives under INBOUND_REFERRALS_FORM_KEY and the single General row under GENERAL_FORM_KEY.
export type PreferencesFormValues = {
  [key: string]: Notification | InboundReferralPreference | GeneralPreference;
};

export type PreferencesFormMeta = {
  isSubmitting: boolean;
};

type NotificationPreferencesProps = {
  user: any;
  isAdmin?: boolean;
  onBack?: () => void;
};

// Whether anything saved would actually send a push: a Push box checked in a row and a category
// that are both switched on. A switched-off row or category sends nothing, whatever is checked in it.
function hasPushSelected(values: FormValues<PreferencesFormValues>): boolean {
  return Object.values(values).some((preference) => {
    if (!preference || preference.active === false) return false;
    const categories = Object.values(preference.notificationSelections ?? {}) as Record<string, unknown>[];
    return categories.some(
      (category) =>
        category?.active !== false &&
        Object.values(category ?? {}).some((kind) => (kind as { push?: boolean } | null)?.push === true)
    );
  });
}

export default function NotificationPreferences({ user, isAdmin = false, onBack }: NotificationPreferencesProps) {
  const queryClient = useQueryClient();
  const { addToast } = useToastActions();
  const { profile } = useProfile();
  const [selectedTab, setSelectedTab] = useState<TabType>();
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [showNoPushDevices, setShowNoPushDevices] = useState(false);
  // Push devices are the signed-in user's own, so an admin viewing another user never checks them.
  const pushEnabled = profile.hasFlag(Flags.PushNotifications) && !isAdmin;
  const push = usePushSubscription({ enabled: pushEnabled });
  const [activeSection, setActiveSection] = useState<'careCoordination' | 'inboundReferrals' | 'general'>(
    'careCoordination'
  );
  const [irNavExpanded, setIrNavExpanded] = useState(false);

  // The main app's SideNavLink semantics: expansion follows the active section — entering the
  // section expands its accordion, leaving it collapses it — while the caret (and a click on the
  // already-active header) can still fold the open section without changing the selection.
  const irNavActive = activeSection === 'inboundReferrals';
  useEffect(() => {
    setIrNavExpanded(irNavActive);
  }, [irNavActive]);

  const handleIrNavClick = () => {
    if (irNavActive) {
      setIrNavExpanded((prev) => !prev);
    } else {
      setActiveSection('inboundReferrals');
    }
  };
  const handleIrNavCaretClick = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setIrNavExpanded((prev) => !prev);
  };

  const actingClientId = user.actingClientId ?? user.actingClient?.id ?? '';
  const effectiveClientId = selectedClient?.id ?? actingClientId;

  // The same client set the Select View (acting client) picker offers: leaf
  // clients only, acting client pinned first then name-sorted — the pin keeps
  // the acting client on the first page even for internal users whose visible
  // set exceeds it. clientGroupTypes rides along for tab config.
  const clientsParams = { leaf: true, include: 'clientGroupTypes', sortBy: 'acting_client_first', pageSize: 100 };
  const { data: actableClients } = useQuery({
    queryKey: [...clientsQueryKeys.index, { ...clientsParams, context: 'notificationPreferences' }],
    queryFn: ({ signal }) => indexClients(clientsParams, signal),
    enabled: !isAdmin,
    // No client-side re-sort: the server's acting-client-first order is the point.
    select: (page) => page.data,
  });

  // The selector's value must be one of its own options. One reachable state
  // breaks that: a user scoped to a client that has since gained children —
  // gaining a child flips a client's leaf flag, dropping it from this list,
  // and the login-time auto-assign never re-fires for already-scoped users.
  // Without this, the Select would sit on its placeholder while the page
  // loads that client's data anyway.
  useEffect(() => {
    if (isAdmin || selectedClient || !actableClients?.length) return;
    if (actableClients.some((client) => client.id === actingClientId)) return;

    setSelectedClient(actableClients[0]);
  }, [isAdmin, selectedClient, actableClients, actingClientId]);

  // The page is keyed entirely to the selector; the acting client is only the
  // selector's initial value.
  const queryParams = useMemo(
    () => ({
      id: user.id,
      include: 'groupType,scopes',
      client: effectiveClientId,
    }),
    [user.id, effectiveClientId]
  );

  const { data: notificationPreferences } = useQuery({
    queryKey: isAdmin
      ? adminUsersQueryKeys.notificationPreferences(user.id)
      : notificationPreferencesQueryKeys.index(queryParams),
    queryFn: async ({ signal }): Promise<AnyNotificationPreference[]> =>
      isAdmin
        ? ((await adminFetchUserNotificationPreferences(user.id, signal)) as unknown as AnyNotificationPreference[])
        : fetchNotificationPreferences(queryParams, signal),
    // Never refetch in the background: a refetch replaces the form's
    // initialValues and resets it, silently discarding unsaved edits. The
    // save path keeps this cache correct itself, and switching clients
    // fetches under a fresh key.
    staleTime: Infinity,
  });

  // Whichever client the selector names; user.actingClient only covers the
  // pre-selection state (and the admin view, which loads no client list).
  const configClient =
    selectedClient ?? actableClients?.find((client) => client.id === effectiveClientId) ?? user.actingClient;

  // The Inbound Referrals nav item rides the DELIVERY flag (inbound_referral_notifications, the
  // same Flipper lever that unmutes the family's sends) — one flag flip turns on both the emails
  // and the preferences that govern them — plus per-CLIENT product access (the selector may
  // address a client without it) and the view grant, since the family only ever notifies users
  // who can see the referrals. The admin read-only view is presence-driven instead: the admin's
  // own profile says nothing about the viewed user, and the api serves the row either way.
  const showInboundReferrals = isAdmin
    ? !!notificationPreferences?.some(isInboundReferralPreference)
    : !!configClient?.inboundReferralsEnabled &&
      profile.hasFlag(Flags.InboundReferralNotifications) &&
      profile.has(PERMISSIONS.workflowInboundReferralView);

  // Every user has General preferences. The admin read-only view shows the section when the api
  // served the row.
  const showGeneral = isAdmin ? !!notificationPreferences?.some(isGeneralPreference) : true;

  // A client switch can land on a client without inbound-referrals access while that section
  // is open — fall back to care coordination rather than an empty main column.
  useEffect(() => {
    if (!showInboundReferrals && activeSection === 'inboundReferrals') setActiveSection('careCoordination');
  }, [showInboundReferrals, activeSection]);

  const { mutateAsync: updatePreferences } = useMutation({
    mutationFn: updateNotificationPreferences,
  });

  const { mutateAsync: createPreferences } = useMutation({
    mutationFn: createNotificationPreferences,
    // Key the patch to the client that was SAVED (off the mutation variables):
    // by the time the POST resolves the selector may already point at another
    // client, and patching the live key would merge this client's row — id
    // included — into that client's cache.
    onSuccess: (data, variables) => {
      const savedParams = { id: user.id, include: 'groupType,scopes', client: variables.clientId };

      queryClient.setQueryData<AnyNotificationPreference[]>(
        notificationPreferencesQueryKeys.index(savedParams),
        (prefs) => {
          return prefs?.map((pref) => {
            if (isCareCoordinationPreference(pref) && pref.groupTypeApiName === data.groupTypeApiName) {
              return { ...pref, ...data } as Notification;
            } else {
              return pref;
            }
          });
        }
      );
    },
  });

  const { mutateAsync: updateIrPreference } = useMutation({
    mutationFn: updateInboundReferralPreference,
  });

  const { mutateAsync: createIrPreference } = useMutation({
    mutationFn: createInboundReferralPreference,
    // Same saved-client keying as the care-coordination create: the point is landing the new
    // row's id in the right cache so the next save updates instead of duplicating.
    onSuccess: (data, variables) => {
      const savedParams = { id: user.id, include: 'groupType,scopes', client: variables.clientId };

      queryClient.setQueryData<AnyNotificationPreference[]>(
        notificationPreferencesQueryKeys.index(savedParams),
        (prefs) => prefs?.map((pref) => (isInboundReferralPreference(pref) ? data : pref))
      );
    },
  });

  const { mutateAsync: updateGeneral } = useMutation({
    mutationFn: updateGeneralPreference,
  });

  const { mutateAsync: createGeneral } = useMutation({
    mutationFn: createGeneralPreference,
    // Same saved-client keying as the other creates, so the next save updates the new row.
    onSuccess: (data, variables) => {
      const savedParams = { id: user.id, include: 'groupType,scopes', client: variables.clientId };

      queryClient.setQueryData<AnyNotificationPreference[]>(
        notificationPreferencesQueryKeys.index(savedParams),
        (prefs) => prefs?.map((pref) => (isGeneralPreference(pref) ? data : pref))
      );
    },
  });

  const initialValues = useMemo(() => {
    return notificationPreferences?.reduce((acc, preference) => {
      if (isInboundReferralPreference(preference)) {
        // Keep the row out of the form when the section is hidden, so a save can never touch
        // preferences the user was not shown.
        if (showInboundReferrals) acc[INBOUND_REFERRALS_FORM_KEY] = preference;
      } else if (isGeneralPreference(preference)) {
        if (showGeneral) acc[GENERAL_FORM_KEY] = preference;
      } else {
        acc[preference.groupType.id] = preference;
      }
      return acc;
    }, {}) as PreferencesFormValues;
  }, [notificationPreferences, showInboundReferrals, showGeneral]);

  const handleSubmit = async (
    values: FormValues<PreferencesFormValues>,
    _meta: FormMeta<PreferencesFormMeta>,
    helpers: FormHelpers<PreferencesFormValues, PreferencesFormMeta>
  ) => {
    helpers.setMeta('isSubmitting', true);

    const requests: Promise<unknown>[] = [];

    for (const key in values) {
      if (isDeepEqual(values[key], initialValues[key])) {
        continue;
      }

      if (key === INBOUND_REFERRALS_FORM_KEY) {
        const preference = values[key] as InboundReferralPreference;
        const payload = {
          id: preference.id || undefined,
          clientId: effectiveClientId,
          active: preference.active,
          type: UserPreferenceType.NOTIFICATION_INBOUND_REFERRALS as const,
          notificationSelections: preference.notificationSelections,
          scopes: {
            referralSources: preference.scopes?.referralSources ?? [],
            groups: preference.scopes?.groups?.map((group) => group.id) ?? [],
          },
        };

        requests.push(preference.id ? updateIrPreference(payload) : createIrPreference(payload));
        continue;
      }

      if (key === GENERAL_FORM_KEY) {
        const preference = values[key] as GeneralPreference;
        const payload = {
          id: preference.id || undefined,
          clientId: effectiveClientId,
          active: preference.active,
          type: UserPreferenceType.NOTIFICATION_GENERAL as const,
          notificationSelections: preference.notificationSelections,
        };

        requests.push(preference.id ? updateGeneral(payload) : createGeneral(payload));
        continue;
      }

      const careCoordination = values[key] as Notification;
      const prefs = {
        ...careCoordination,
        scopes: {
          groups: careCoordination?.scopes.groups?.map((s) => s.id) ?? [],
          planTypes: careCoordination?.scopes.planTypes?.map((s) => s.id) ?? [],
          episodes: careCoordination?.scopes.episodes?.map((s) => s.id) ?? [],
          caseManagers: careCoordination?.scopes.caseManagers?.map((s) => s.id) ?? [],
          utilizationManagers: careCoordination?.scopes.utilizationManagers?.map((s) => s.id) ?? [],
          physicianTeams: careCoordination?.scopes.physicianTeams?.map((s) => s.id) ?? [],
        },
      } as NotificationOptions;

      if (prefs.id) {
        requests.push(updatePreferences({ ...prefs, urlExtension: `/${prefs.id}`, include: 'groupType,scopes' }));
      } else {
        requests.push(
          createPreferences({
            ...prefs,
            clientId: effectiveClientId,
            include: 'groupType,scopes',
          })
        );
      }
    }

    queryClient.setQueryData<AnyNotificationPreference[]>(
      notificationPreferencesQueryKeys.index(queryParams),
      Object.values(values) as AnyNotificationPreference[]
    );

    const outcomes = await Promise.allSettled(requests);

    if (outcomes.some((outcome) => outcome.status === 'rejected')) {
      // The optimistic cache write above no longer reflects the server —
      // refetch the truth and tell the user instead of a false success.
      queryClient.invalidateQueries({ queryKey: notificationPreferencesQueryKeys.index(queryParams) });
      addToast({ text: "Some changes couldn't be saved. Please try again." });
      return;
    }

    addToast({ text: 'Your changes have been saved!' });

    // A checked Push box with nowhere to deliver is saved all the same; say so once it is. Only on a
    // device list that actually loaded — a failed load is not "no devices".
    const hasReceivingDevice = push.devices.some((device) =>
      isReceiving(device, { isThisDevice: device.id === push.thisDevice?.id, permission: push.permission })
    );
    const devicesLoaded = !push.loading && !push.loadError;
    if (pushEnabled && devicesLoaded && !hasReceivingDevice && hasPushSelected(values)) {
      setShowNoPushDevices(true);
    }
  };

  const tabs = useMemo(() => {
    // Tabs come straight from the server, which resolves the selected client's
    // enabled types and already honors the user's group-type selections. The
    // inbound-referrals and General rows are not tabs — they have no group type;
    // each renders under its own left-nav section.
    const vals =
      notificationPreferences?.filter(isCareCoordinationPreference).map((preference) => ({
        label: preference.groupType.displayName,
        value: preference.groupType.id,
      })) ?? [];

    // Also re-seat a tab that dropped out of the set (e.g. a refetch shrank
    // the group types) — a stale value renders an empty, disabled form.
    if (!selectedTab || !vals.some((tab) => tab.value === selectedTab.value)) setSelectedTab(vals[0]);
    return vals;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationPreferences]);

  const configForSelectedGroupType = configClient?.configForGroupType(selectedTab?.value || '');
  const caseManagerEnabled = !!configForSelectedGroupType?.caseManager;
  const utilizationManagerEnabled = !!configForSelectedGroupType?.utilizationManager;

  const handleClientChange = (client: Client) => {
    setSelectedClient(client);
    setSelectedTab(undefined);
  };

  const FormActions = ({ dirty, meta }) => {
    const { openDiscardChangesModal } = useDiscardChanges();

    const handleDiscardChanges = () => {
      openDiscardChangesModal({ navigatingAway: false });
    };

    return (
      <StyledButtonGroup>
        <Button
          variant='ghost'
          data-cy='clickable'
          disabled={!dirty || meta.isSubmitting}
          onClick={handleDiscardChanges}>
          Discard Changes
        </Button>
        <Button data-cy='clickable' disabled={!dirty} loading={meta.isSubmitting} type='submit'>
          Save Changes
        </Button>
      </StyledButtonGroup>
    );
  };

  const showClientSelector = !isAdmin && (actableClients?.length ?? 0) > 1;

  // Mobile has no left nav, so Inbound Referrals rides the tab strip as a last tab there;
  // desktop keeps the strip to group types and switches sections through the nav.
  const inboundReferralsTab: TabType = { label: 'Inbound Referrals', value: INBOUND_REFERRALS_FORM_KEY };
  const generalTab: TabType = { label: 'General', value: GENERAL_FORM_KEY };
  const mobileTabs = [
    ...(showGeneral ? [generalTab] : []),
    ...tabs,
    ...(showInboundReferrals ? [inboundReferralsTab] : []),
  ];
  const handleMobileTabClick = (tab: TabType) => {
    if (tab.value === INBOUND_REFERRALS_FORM_KEY) {
      setActiveSection('inboundReferrals');
    } else if (tab.value === GENERAL_FORM_KEY) {
      setActiveSection('general');
    } else {
      setActiveSection('careCoordination');
      setSelectedTab(tab);
    }
  };
  const selectedMobileTab =
    activeSection === 'inboundReferrals'
      ? inboundReferralsTab
      : activeSection === 'general'
        ? generalTab
        : tabs.find((tab) => tab.value === selectedTab?.value) || tabs[0];

  return (
    <TranslationProvider
      groupType={selectedTab && (initialValues?.[selectedTab.value] as Notification | undefined)?.groupType}>
      <FormProvider<PreferencesFormValues, PreferencesFormMeta>
        initialValues={initialValues}
        resetOnInitialValuesChange
        withDiscardChanges
        initialMeta={{ isSubmitting: false }}
        onSubmit={isAdmin ? () => {} : handleSubmit}>
        {({ dirty, meta }) => (
          <StyledForm>
            <Page>
              <DesktopOnly>
                <Page.Header>
                  <TopSection>
                    <TitleSection>
                      {isAdmin ? (
                        <>
                          <Page.Title>{user.name}</Page.Title>
                          <Subtitle>
                            {user.actingClient?.name} — {user.role?.name}
                          </Subtitle>
                        </>
                      ) : (
                        <>
                          <Page.Title>Notification Preferences</Page.Title>
                          {showClientSelector ? (
                            <ClientSelector
                              clients={actableClients ?? []}
                              selectedClientId={effectiveClientId}
                              dirty={dirty}
                              onChange={handleClientChange}
                            />
                          ) : (
                            <Subtitle>{(actableClients?.[0] ?? user.actingClient)?.name}</Subtitle>
                          )}
                        </>
                      )}
                    </TitleSection>
                    {isAdmin ? (
                      <Button variant='ghost' onClick={onBack}>
                        Back to Users
                      </Button>
                    ) : (
                      <FormActions {...{ dirty, meta }} />
                    )}
                  </TopSection>
                </Page.Header>
              </DesktopOnly>
              {!isAdmin && (
                <MobileOnly>
                  <StyledMobilePageHeader
                    title='Notification Preferences'
                    actionElement={
                      <StyledSaveButton variant='ghost' type='submit' data-cy='clickable' disabled={!dirty}>
                        Save
                      </StyledSaveButton>
                    }>
                    <Tabs tabs={mobileTabs} onTabClick={handleMobileTabClick} selectedTab={selectedMobileTab} />
                  </StyledMobilePageHeader>
                </MobileOnly>
              )}
              <StyledPageContent>
                <ContentRow>
                  <DesktopOnly>
                    <LeftNav>
                      {showGeneral && (
                        <NavItem
                          $active={activeSection === 'general'}
                          role='button'
                          tabIndex={0}
                          data-cy='clickable'
                          data-testid='general-nav'
                          onClick={() => setActiveSection('general')}>
                          General
                        </NavItem>
                      )}
                      <NavItem
                        $active={activeSection === 'careCoordination'}
                        role='button'
                        tabIndex={0}
                        data-cy='clickable'
                        onClick={() => setActiveSection('careCoordination')}>
                        Care Coordination
                      </NavItem>
                      {showInboundReferrals && (
                        <>
                          <NavAccordionHeader
                            role='button'
                            tabIndex={0}
                            aria-expanded={irNavExpanded}
                            data-cy='clickable'
                            data-testid='inbound-referrals-nav'
                            onClick={handleIrNavClick}>
                            Inbound Referrals
                            <NavChevron onClick={handleIrNavCaretClick}>
                              {irNavExpanded ? <ChevronDownBold /> : <ChevronRightBold />}
                            </NavChevron>
                          </NavAccordionHeader>
                          {irNavExpanded && (
                            <NavChildItem
                              $active={irNavActive}
                              role='button'
                              tabIndex={0}
                              data-testid='active-referrals-nav'
                              onClick={() => setActiveSection('inboundReferrals')}>
                              Active Referrals
                            </NavChildItem>
                          )}
                        </>
                      )}
                    </LeftNav>
                  </DesktopOnly>
                  <MainColumn>
                    {activeSection === 'careCoordination' && (
                      <DesktopOnly>
                        <TabContainer>
                          <Tabs
                            tabs={tabs}
                            onTabClick={setSelectedTab}
                            selectedTab={tabs.find((tab) => tab.value === selectedTab?.value) || tabs[0]}
                          />
                        </TabContainer>
                      </DesktopOnly>
                    )}
                    <ScrollArea>
                      {activeSection === 'careCoordination' && selectedTab && (
                        <NotificationPreferencesContainer
                          clientId={effectiveClientId}
                          caseManagerEnabled={caseManagerEnabled}
                          utilizationManagerEnabled={utilizationManagerEnabled}
                          selectedGroupType={selectedTab}
                          canActAsManager={user.canActAsManager}
                          disabled={isAdmin}
                          showInboundReferrals={showInboundReferrals}
                        />
                      )}
                      {activeSection === 'inboundReferrals' && (
                        <InboundReferralPreferencesContainer clientId={effectiveClientId} disabled={isAdmin} />
                      )}
                      {activeSection === 'general' && (
                        <GeneralPreferencesContainer disabled={isAdmin} showDevices={!isAdmin} />
                      )}
                    </ScrollArea>
                  </MainColumn>
                </ContentRow>
              </StyledPageContent>
            </Page>
            {showNoPushDevices && <NoPushDevicesModal onClose={() => setShowNoPushDevices(false)} />}
          </StyledForm>
        )}
      </FormProvider>
    </TranslationProvider>
  );
}

const TopSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  padding: 24px;
`;

// Flush, full-bleed two-column body (per the UX-473 mock): the page's own
// gutter is removed so the left nav meets the page's left edge, and the
// columns manage their own spacing.
const StyledPageContent = styled(Page.Content)`
  margin-top: 0;
  padding: 0;
`;

const ContentRow = styled.div`
  background: ${({ theme }) => theme.colors.white};
  display: flex;
  height: 100%;
  overflow: hidden;
`;

const LeftNav = styled.nav`
  border-right: 1px solid ${({ theme }) => theme.colors.black10};
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-shrink: 0;
  width: 220px;
  padding: 24px 16px;
`;

const NavItem = styled.div<{ $active?: boolean }>`
  border-radius: 6px;
  color: ${({ $active, theme }) => ($active ? theme.colors.blue100 : theme.colors.black100)};
  background: ${({ $active, theme }) => ($active ? theme.colors.blue10 : 'transparent')};
  cursor: pointer;
  font-size: 14px;
  font-weight: 400;
  padding: 8px 12px;
`;

// The accordion header stays unhighlighted — selection shows on its child item.
const NavAccordionHeader = styled.div`
  align-items: center;
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.black100};
  cursor: pointer;
  display: flex;
  font-size: 14px;
  font-weight: 400;
  justify-content: space-between;
  padding: 8px 12px;
`;

const NavChevron = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.colors.black100};
  display: flex;
  flex-shrink: 0;
  width: 12px;
`;

const NavChildItem = styled(NavItem)`
  margin-left: 12px;
`;

const MainColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

// The tabs stay put; everything under them is the page's one scrolling region —
// a light-gray backdrop the white cards sit on (per the UX-473 mock).
const ScrollArea = styled.div`
  background: ${({ theme }) => theme.colors.black5};
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

const TabContainer = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.black10};
  padding: 0 24px;
`;

const TitleSection = styled.div``;

const StyledButtonGroup = styled(ButtonGroup)`
  & > button {
    font-size: 16px;
    font-weight: 500;
  }
`;

const Subtitle = styled(H2)`
  color: ${({ theme }) => theme.colors.blue100};
  font-size: 16px;
  margin-top: 12px;
`;

const StyledSaveButton = styled(Button)`
  padding: 0;
  color: ${({ disabled, theme }) => (disabled ? theme.colors.blue25 : theme.colors.blue100)};
  font-size: 14px;
`;

const StyledMobilePageHeader = styled(MobilePageHeader)`
  padding-bottom: 0px;
`;

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  height: 100%;
`;
